// Pure file-classification helpers shared across apps and the media uploader.
// No Supabase or DOM-storage dependency — just File metadata in, kind out.
// MediaKind / MEDIA_KINDS live in ./types (the single source of truth) and are
// re-exported here for convenience.
import type { MediaKind } from './types';
export { MEDIA_KINDS, isMediaKind } from './types';
export type { MediaKind } from './types';

// The concrete media_parts.part_type for an uploaded file (never 'any').
export type PartKind = 'image' | 'video' | 'audio' | 'file';

const EXTENSION_MIME_FALLBACK: Record<string, string> = {
  md: 'text/markdown',
  markdown: 'text/markdown',
  txt: 'text/plain',
  json: 'application/json',
  yaml: 'text/yaml',
  yml: 'text/yaml',
  ts: 'text/x-typescript',
  tsx: 'text/x-typescript',
  js: 'text/javascript',
  py: 'text/x-python',
  pdf: 'application/pdf',
};

export function extension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot < 0 || dot === filename.length - 1) return '';
  return filename.slice(dot + 1).toLowerCase();
}

export function resolveMime(file: File): string {
  if (file.type && file.type.length > 0) return file.type;
  const ext = extension(file.name);
  return EXTENSION_MIME_FALLBACK[ext] || 'application/octet-stream';
}

export function classifyKind(mime: string, filename: string): PartKind {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  const ext = extension(filename);
  if (ext === 'mp3' || ext === 'wav' || ext === 'm4a' || ext === 'ogg' || ext === 'flac') {
    return 'audio';
  }
  return 'file';
}

export function classifyFile(file: File): { mime: string; kind: PartKind } {
  const mime = resolveMime(file);
  return { mime, kind: classifyKind(mime, file.name) };
}

export function slugifyFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/-+/g, '-').slice(0, 120);
}

// The accept= attribute for a file input given a media_kind constraint.
export function acceptForKind(kind: MediaKind): string | undefined {
  switch (kind) {
    case 'image': return 'image/*';
    case 'video': return 'video/*';
    case 'audio': return 'audio/*';
    case 'file': return undefined; // any file
    case 'any': return undefined;
  }
}
